import * as Constants from '~/utils/Constants'
import * as OWUtils from '~/utils/OWUtils'
import { Global } from '~/global'
import { CustomerType, Maybe } from '~/generated/types'
import { CustomerSearchIndex } from '~/models/CustomerSearchIndex'
import { ServerApi } from '~/utils/ServerApi'

export class CustomersModel {
    static _instance?: CustomersModel

    private prefetched: boolean
    private readonly customers: { [customerId: number]: CustomerType }
    private readonly freshInserts: { [customerId: number]: boolean }
    private readonly searchIndex: CustomerSearchIndex

    constructor() {
        this.prefetched = false

        // The actual cache map; id(Number) -> customer(CustomerType)
        this.customers = {}

        // Tracks inserts from this browser session, to avoid flushing fresh data; id(Number) -> true
        this.freshInserts = {}

        this.searchIndex = CustomerSearchIndex.getInstance()

        // Preload cache with prefetch API
        if (!OWUtils.hasFeature(Constants.Features.DISABLE_PRELOADING)) {
            this.prefetchCustomers()
        }
    }

    static getInstance() {
        if (!this._instance) {
            this._instance = new CustomersModel()
        }
        return this._instance
    }

    /**
     * Populate the cache by fetching customers from the prefetch endpoint
     */
    prefetchCustomers() {
        if (this.prefetched || !Global.OW_AUTH_DATA.employee?.id) {
            return
        }

        void ServerApi.prefetchCustomers().then(() => (this.prefetched = true))
    }

    /**
     * Insert customers into the cache
     * @param customers The customers
     * @param [stale] If present, the inserted data is stale
     */
    _insertCustomers(customers: Maybe<Partial<CustomerType>>[], stale = false) {
        customers.map(e => {
            // Merge on existing
            const existing = this._getCustomer(e?.customerId)
            const merged = Object.assign({}, existing, e)
            this.customers[e?.customerId] = merged
            if (!stale) {
                this.freshInserts[e?.customerId] = true
            }

            if (e?.customerId !== false) {
                this.searchIndex.insertCustomer(merged)
            }

            return merged
        })
    }

    /**
     * Insert a customer into the cache
     * @param c The customer
     */
    static insertCustomer(c: Maybe<Partial<CustomerType>>) {
        CustomersModel.getInstance()._insertCustomers([c])
    }

    /**
     * Get a customer from the cache
     * @param customerId The customerId of the employee
     * @returns The customer, or undefined if they are not in the cache
     */
    _getCustomer(customerId: number): CustomerType | undefined {
        return this.customers[customerId]
    }

    /**
     * Get a customer from the cache
     * @param customerId The customerId of the customer
     * @returns The customer, or undefined if they are not in the cache
     */
    static getCustomer(customerId: number): CustomerType | undefined {
        return CustomersModel.getInstance()._getCustomer(customerId)
    }

    /**
     * Search the cached customers
     * @param  query The search query
     * @returns  The cached customers that match the query
     */
    searchCustomers(query: string): (CustomerType | undefined)[] {
        return this.searchIndex.search(query).map(({ customerId }) => this._getCustomer(customerId))
    }
}
